import React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Card,
  CardContent,
  Button,
  useTheme,
  alpha,
} from "@mui/material";
import {
  MovieFilter,
  SelfImprovement,
  Group,
  CameraAlt,
  Psychology,
  HistoryEdu,
  Computer,
  ChevronRight,
  Forum,
} from "@mui/icons-material";

const GOLD = "#D4AF37";

export default function FilmEdit() {
  const theme = useTheme();

  const tips = [
    {
      title: "Take breaks and review frequently",
      desc: "Check often for new noises or environment changes. Be aware of your own energy—filming can be exhausting, and that shows on screen.",
      icon: <SelfImprovement />,
    },
    {
      title: "Build rapport",
      desc: "Students want to know their teacher. Even for screencasts, film yourself for the intro. Introducing each section on camera adds a personal touch.",
      icon: <Group />,
    },
    {
      title: "Being on camera takes practice",
      desc: "The more you do it, the more natural you'll feel. Record a few trial runs before filming the real content to find your rhythm.",
      icon: <CameraAlt />,
    },
    {
      title: "Set yourself up for editing success",
      desc: "Plan to edit out pauses and mistakes. Film extra visuals (B-roll) or activities to cover cuts and keep the video engaging.",
      icon: <Psychology />,
    },
    {
      title: "Create audio marks",
      desc: "Clap when you start each take. This creates a clear audio spike that makes sync and editing much faster later.",
      icon: <HistoryEdu />,
    },
    {
      title: "For screencasts, clean your space",
      desc: "Hide unrelated folders and close unneeded tabs. Ensure on-screen text is large (24pt+) and use zoom to highlight important areas.",
      icon: <Computer />,
    },
  ];

  const requirements = [
    "Film and export in HD (at least 720p, preferably 1080p)",
    "Clean audio synced to video, coming from both channels",
    "Engaging delivery with clear, high-quality audio",
  ];

  const resources = [
    {
      title: "Create a test video",
      desc: "Get feedback before filming your whole course",
    },
    {
      title: "Teaching Center: Quality A/V",
      desc: "Film and edit with confidence",
    },
    {
      title: "Join the community",
      desc: "Get production help from fellow instructors",
    },
  ];

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>
      <Typography variant="h4" fontWeight={900} gutterBottom>
        Film & Edit
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 6 }}>
        Share your knowledge with the world. With good preparation and our
        guides, you're ready to create high-quality content.
      </Typography>

      <Grid container spacing={6}>
        <Grid item xs={12} md={8}>
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}
            >
              <MovieFilter sx={{ color: GOLD }} />
              Filming Tips
            </Typography>
            <Stack spacing={4}>
              {tips.map((tip, idx) => (
                <Box key={idx} sx={{ display: "flex", gap: 3 }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      bgcolor: alpha(GOLD, 0.1),
                      color: GOLD,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {tip.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      gutterBottom
                    >
                      {tip.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {tip.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Quality Standards
            </Typography>
            <List sx={{ bgcolor: alpha(GOLD, 0.02), borderRadius: 4, p: 2 }}>
              {requirements.map((req, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon sx={{ minWidth: 32, color: GOLD }}>
                    <ChevronRight fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={req}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 500,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <Box>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Production Resources
            </Typography>
            <Grid container spacing={3}>
              {resources.map((res, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Link href="#" underline="none">
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        transition: "0.2s",
                        "&:hover": {
                          borderColor: GOLD,
                          bgcolor: alpha(GOLD, 0.05),
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={800}
                        color="primary"
                        gutterBottom
                      >
                        {res.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {res.desc}
                      </Typography>
                    </Paper>
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 6,
              textAlign: "center",
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
              position: "sticky",
              top: 20,
            }}
          >
            <CardContent>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  mx: "auto",
                  mb: 2,
                  bgcolor: alpha(GOLD, 0.1),
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Forum sx={{ fontSize: 60, color: GOLD }} />
              </Box>
              <Typography variant="h6" fontWeight={800} gutterBottom>
                Instructor Community
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Connect with other instructors to get production help and share
                your progress.
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                sx={{
                  borderRadius: "50px",
                  borderColor: GOLD,
                  color: GOLD,
                  fontWeight: 700,
                  "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.05) },
                }}
              >
                Join the community
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
